import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Upload, 
  Code,
  Plus,
  Loader2,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubmitConnectorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [industries, setIndustries] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    service_name: '',
    description: '',
    industry: '',
    category: '',
    authentication_method: 'API Key',
    documentation: '',
    supported_actions: [
      { name: '', description: '', inputs: [], output_schema: {}, example_prompt: '' }
    ],
    permissions_required: [],
    rate_limits: '',
    icon_url: '',
    github_url: '',
    api_docs_url: ''
  });

  const [newPermission, setNewPermission] = useState('');

  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to submit a connector');
      navigate('/');
    }
    
    const fetchIndustries = async () => {
      try {
        const response = await axios.get(`${API}/connectors/industries`);
        setIndustries(response.data.industries || []);
      } catch (error) {
        console.error('Error fetching industries:', error);
      }
    };
    fetchIndustries();
  }, [user, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleActionChange = (index, field, value) => {
    const newActions = [...formData.supported_actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setFormData(prev => ({ ...prev, supported_actions: newActions }));
  };

  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      supported_actions: [
        ...prev.supported_actions,
        { name: '', description: '', inputs: [], output_schema: {}, example_prompt: '' }
      ]
    }));
  };

  const removeAction = (index) => {
    if (formData.supported_actions.length > 1) {
      setFormData(prev => ({
        ...prev,
        supported_actions: prev.supported_actions.filter((_, i) => i !== index)
      }));
    }
  };

  const addPermission = () => {
    if (newPermission.trim()) {
      setFormData(prev => ({
        ...prev,
        permissions_required: [...prev.permissions_required, newPermission.trim()]
      }));
      setNewPermission('');
    }
  };

  const removePermission = (index) => {
    setFormData(prev => ({
      ...prev,
      permissions_required: prev.permissions_required.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, formData);
      toast.success('Connector submitted for review!');
      navigate('/community');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit connector');
    } finally {
      setSubmitting(false);
    }
  };

  const isStep1Valid = formData.name && formData.service_name && formData.description && formData.industry && formData.category;
  const isStep2Valid = formData.supported_actions.every(a => a.name && a.description && a.example_prompt);
  const isStep3Valid = formData.documentation.length > 50;

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Community Contribution
          </Badge>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white mb-4">
            Submit a Connector
          </h1>
          <p className="text-lg text-slate-400">
            Share your connector with the community. Help other developers connect their AI agents.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step === s ? 'bg-primary text-white' : 
                step > s ? 'bg-accent text-white' : 'bg-secondary text-slate-400'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && <div className={`w-20 h-1 mx-2 ${step > s ? 'bg-accent' : 'bg-secondary'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-1">
            <div className="bg-card border border-white/5 rounded-xl p-6 space-y-6">
              <h2 className="font-heading text-xl font-semibold text-white">Basic Information</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Connector Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Notion"
                    className="bg-secondary border-border"
                    data-testid="connector-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Service Name *</label>
                  <Input
                    value={formData.service_name}
                    onChange={(e) => handleInputChange('service_name', e.target.value)}
                    placeholder="e.g., NotionAPI"
                    className="bg-secondary border-border"
                    data-testid="service-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what this connector does and what software it connects to..."
                  className="bg-secondary border-border min-h-24"
                  data-testid="description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Industry *</label>
                  <Select value={formData.industry} onValueChange={(v) => handleInputChange('industry', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Category *</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    placeholder="e.g., Note Taking, Project Management"
                    className="bg-secondary border-border"
                    data-testid="category-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Auth Method</label>
                  <Select value={formData.authentication_method} onValueChange={(v) => handleInputChange('authentication_method', v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="API Key">API Key</SelectItem>
                      <SelectItem value="OAuth2">OAuth2</SelectItem>
                      <SelectItem value="Basic Auth">Basic Auth</SelectItem>
                      <SelectItem value="Bearer Token">Bearer Token</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Rate Limits</label>
                  <Input
                    value={formData.rate_limits}
                    onChange={(e) => handleInputChange('rate_limits', e.target.value)}
                    placeholder="e.g., 100 requests/min"
                    className="bg-secondary border-border"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Icon URL</label>
                  <Input
                    value={formData.icon_url}
                    onChange={(e) => handleInputChange('icon_url', e.target.value)}
                    placeholder="https://..."
                    type="url"
                    className="bg-secondary border-border"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">GitHub URL</label>
                  <Input
                    value={formData.github_url}
                    onChange={(e) => handleInputChange('github_url', e.target.value)}
                    placeholder="https://github.com/..."
                    type="url"
                    className="bg-secondary border-border"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                onClick={() => setStep(2)}
                disabled={!isStep1Valid}
                className="bg-primary hover:bg-primary/90"
                data-testid="next-step-btn"
              >
                Next: Define Actions
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Actions */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-2">
            <div className="bg-card border border-white/5 rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold text-white">Supported Actions</h2>
                <Button variant="outline" size="sm" onClick={addAction} className="border-slate-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Action
                </Button>
              </div>

              {formData.supported_actions.map((action, index) => (
                <div key={index} className="bg-secondary/50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-white">Action {index + 1}</h3>
                    {formData.supported_actions.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeAction(index)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-400 mb-1 block">Action Name *</label>
                      <Input
                        value={action.name}
                        onChange={(e) => handleActionChange(index, 'name', e.target.value)}
                        placeholder="e.g., get_pages"
                        className="bg-secondary border-border font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-400 mb-1 block">Description *</label>
                      <Input
                        value={action.description}
                        onChange={(e) => handleActionChange(index, 'description', e.target.value)}
                        placeholder="What does this action do?"
                        className="bg-secondary border-border"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Example Prompt *</label>
                    <Input
                      value={action.example_prompt}
                      onChange={(e) => handleActionChange(index, 'example_prompt', e.target.value)}
                      placeholder="e.g., Show me all my Notion pages"
                      className="bg-secondary border-border"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Permissions */}
            <div className="bg-card border border-white/5 rounded-xl p-6 space-y-4">
              <h2 className="font-heading text-xl font-semibold text-white">Required Permissions</h2>
              
              <div className="flex gap-2">
                <Input
                  value={newPermission}
                  onChange={(e) => setNewPermission(e.target.value)}
                  placeholder="e.g., pages:read"
                  className="bg-secondary border-border font-mono"
                  onKeyDown={(e) => e.key === 'Enter' && addPermission()}
                />
                <Button onClick={addPermission} variant="outline" className="border-slate-700">
                  Add
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.permissions_required.map((perm, index) => (
                  <Badge key={index} variant="outline" className="border-slate-700 text-slate-300 px-3 py-1">
                    {perm}
                    <button onClick={() => removePermission(index)} className="ml-2 hover:text-white">×</button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="border-slate-700">
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)}
                disabled={!isStep2Valid}
                className="bg-primary hover:bg-primary/90"
              >
                Next: Documentation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Documentation */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-3">
            <div className="bg-card border border-white/5 rounded-xl p-6 space-y-6">
              <h2 className="font-heading text-xl font-semibold text-white">Documentation</h2>
              <p className="text-sm text-slate-400">
                Write clear documentation to help other developers use your connector. Markdown is supported.
              </p>
              
              <Textarea
                value={formData.documentation}
                onChange={(e) => handleInputChange('documentation', e.target.value)}
                placeholder={`# ${formData.name || 'Connector'} Connector

## Overview
Describe what this connector does...

## Authentication
Explain how to authenticate...

## Actions
List available actions...

## Examples
Provide code examples...`}
                className="bg-secondary border-border min-h-80 font-mono text-sm"
                data-testid="documentation-input"
              />

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">API Documentation URL</label>
                <Input
                  value={formData.api_docs_url}
                  onChange={(e) => handleInputChange('api_docs_url', e.target.value)}
                  placeholder="https://docs.example.com/api"
                  type="url"
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="border-slate-700">
                Back
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!isStep3Valid || submitting}
                className="glow-primary bg-primary hover:bg-primary/90"
                data-testid="submit-connector-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit Connector
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitConnectorPage;
